import { Building, Wrench, Zap, CheckCircle, Phone, Star, Shield, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '@/components/ui-custom/SEO';

const Commercial = () => {
  const services = [
    {
      title: 'Tenant Improvements',
      description: 'Complete plumbing and electrical work for office build-outs and renovations.',
      icon: Building,
    },
    {
      title: 'Preventive Maintenance',
      description: 'Scheduled maintenance programs to keep your systems running smoothly.',
      icon: Wrench,
    },
    {
      title: 'Code Compliance',
      description: 'Ensure your business meets all local and state electrical and plumbing codes.',
      icon: Shield,
    },
    {
      title: 'Emergency Repairs',
      description: 'Fast response times to minimize downtime and business disruption.',
      icon: Clock,
    },
    {
      title: 'Commercial Lighting',
      description: 'Energy-efficient lighting solutions for offices, retail, and industrial spaces.',
      icon: Zap,
    },
    {
      title: 'Commercial Plumbing',
      description: 'Restroom fixtures, water lines, and drainage systems for businesses.',
      icon: Wrench,
    },
  ];

  const industries = [
    'Office Buildings',
    'Retail Stores',
    'Restaurants',
    'Medical Facilities',
    'Industrial Facilities',
    'Multi-Family Properties',
  ];

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'serviceType': 'Commercial Plumbing and Electrical',
    'name': 'Commercial Plumbing & Electrical Services in Dothan, AL',
    'description': 'Commercial plumbing and electrical services including tenant improvements, preventive maintenance, code compliance, emergency repairs, and commercial lighting throughout the Wiregrass area.',
    'provider': { '@type': 'LocalBusiness', 'name': "Ard's Plumbing & Electric", 'telephone': '+1-334-692-5537', 'url': 'https://ardsdothan.com' },
    'areaServed': { '@type': 'AdministrativeArea', 'name': 'Wiregrass Area, Alabama' },
    'url': 'https://ardsdothan.com/services/commercial',
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Commercial Services',
      'itemListElement': [
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Tenant Improvements' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Preventive Maintenance' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Code Compliance' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Emergency Repairs' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Commercial Lighting' } },
        { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Commercial Plumbing' } },
      ],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      { '@type': 'Question', 'name': 'Do you work with property managers and landlords?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, we work with property managers, landlords, and businesses of all sizes throughout the Wiregrass area.' } },
      { '@type': 'Question', 'name': 'Can you handle emergency commercial repairs?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, we prioritize emergency commercial repairs to minimize business downtime. Call (334) 692-5537 for fast response.' } },
      { '@type': 'Question', 'name': 'Do you offer preventive maintenance programs?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, we offer scheduled maintenance programs tailored to your business needs to keep your systems running smoothly.' } },
      { '@type': 'Question', 'name': 'Are you licensed for commercial work in Alabama?', 'acceptedAnswer': { '@type': 'Answer', 'text': "Yes, Ard's Plumbing & Electric is fully licensed and insured for commercial plumbing and electrical work in Alabama." } },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://ardsdothan.com/' },
      { '@type': 'ListItem', 'position': 2, 'name': 'Services', 'item': 'https://ardsdothan.com/services' },
      { '@type': 'ListItem', 'position': 3, 'name': 'Commercial', 'item': 'https://ardsdothan.com/services/commercial' },
    ],
  };

  const faqs = [
    { q: 'Do you work with property managers and landlords?', a: 'Yes, we work with property managers, landlords, and businesses of all sizes throughout the Wiregrass area.' },
    { q: 'Can you handle emergency commercial repairs?', a: 'Yes, we prioritize emergency commercial repairs to minimize business downtime. Call (334) 692-5537 for fast response.' },
    { q: 'Do you offer preventive maintenance programs?', a: 'Yes, we offer scheduled maintenance programs tailored to your business needs to keep your systems running smoothly.' },
    { q: "Are you licensed for commercial work in Alabama?", a: "Yes, Ard's Plumbing & Electric is fully licensed and insured for commercial plumbing and electrical work in Alabama." },
  ];

  return (
    <>
      <SEO
        title="Commercial Plumbing & Electrical Dothan AL | Ard's"
        description="Commercial plumbing and electrical services in Dothan, AL. Tenant improvements, maintenance, code compliance & more. Call (334) 692-5537."
        keywords="commercial plumber Dothan, commercial electrician, business plumbing, tenant improvements, commercial electrical repair, industrial plumbing"
        canonical="/services/commercial"
        additionalSchema={[serviceSchema, faqSchema, breadcrumbSchema]}
      />

      {/* Hero Section */}
      <section className="bg-brand-blue pt-32 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 text-white text-sm px-4 py-2 rounded-full mb-6">
                <Building className="w-4 h-4" />
                Commercial Services
              </div>
              <h1 className="font-heading font-black text-4xl lg:text-6xl text-white leading-tight mb-6">
                COMMERCIAL<br />
                <span className="text-brand-yellow">SOLUTIONS</span>
              </h1>
              <p className="text-lg text-white/90 mb-8 max-w-lg">
                Professional plumbing and electrical services to keep your business 
                operating with minimal downtime. Serving Dothan and the Wiregrass.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  className="bg-brand-coral hover:bg-brand-coral/90 text-white rounded-full px-8 py-6 text-lg font-semibold"
                >
                  <Link to="/contact-us">Get a Quote</Link>
                </Button>
                <a
                  href="tel:3346925537"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border-2 border-white text-white font-semibold hover:bg-white hover:text-brand-blue transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  (334) 692-5537
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="/images/commercial-service.jpg"
                alt="Commercial plumbing and electrical services in Dothan AL"
                className="rounded-[28px] shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-brand-blue rounded-full flex items-center justify-center">
                    <Star className="w-6 h-6 text-white fill-white" />
                  </div>
                  <div>
                    <p className="font-bold text-brand-black">4+ Rating</p>
                    <p className="text-sm text-gray-500">Business Trusted</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-black text-3xl lg:text-5xl text-brand-black mb-4">
              COMMERCIAL SERVICES
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive plumbing and electrical solutions for businesses throughout the Wiregrass area.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-6 hover:shadow-xl transition-shadow"
              >
                <div className="w-14 h-14 bg-brand-blue/10 rounded-xl flex items-center justify-center mb-4">
                  <service.icon className="w-7 h-7 text-brand-blue" />
                </div>
                <h3 className="font-heading font-bold text-xl text-brand-black mb-2">
                  {service.title}
                </h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 px-4 bg-brand-navy">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-black text-3xl lg:text-5xl text-white mb-6">
                INDUSTRIES<br />
                <span className="text-brand-yellow">WE SERVE</span>
              </h2>
              <p className="text-lg text-white/80 mb-8">
                We work with businesses across many industries, providing reliable 
                plumbing and electrical services tailored to your specific needs.
              </p>
              <ul className="grid grid-cols-2 gap-4">
                {industries.map((industry, index) => (
                  <li key={index} className="flex items-center gap-3 text-white">
                    <CheckCircle className="w-5 h-5 text-brand-yellow flex-shrink-0" />
                    <span>{industry}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <Building className="w-10 h-10 text-brand-yellow mx-auto mb-3" />
                <p className="font-bold text-white text-lg">Business</p>
                <p className="text-white/70 text-sm">Focused</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <Clock className="w-10 h-10 text-brand-yellow mx-auto mb-3" />
                <p className="font-bold text-white text-lg">Minimal</p>
                <p className="text-white/70 text-sm">Downtime</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <Shield className="w-10 h-10 text-brand-yellow mx-auto mb-3" />
                <p className="font-bold text-white text-lg">Code</p>
                <p className="text-white/70 text-sm">Compliant</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <Star className="w-10 h-10 text-brand-yellow mx-auto mb-3" />
                <p className="font-bold text-white text-lg">5-Star</p>
                <p className="text-white/70 text-sm">Rated</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading font-black text-3xl lg:text-5xl text-brand-black mb-4">
              FREQUENTLY ASKED QUESTIONS
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-heading font-bold text-lg text-brand-black mb-2">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-brand-coral">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-black text-3xl lg:text-5xl text-white mb-6">
            GET YOUR FREE QUOTE
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Contact Ard's Plumbing & Electric today for commercial plumbing and electrical 
            services in Dothan and the Wiregrass area.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              className="bg-white text-brand-coral hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold"
            >
              <Link to="/contact-us">Request a Quote</Link>
            </Button>
            <a
              href="tel:3346925537"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border-2 border-white text-white font-semibold hover:bg-white hover:text-brand-coral transition-colors"
            >
              <Phone className="w-5 h-5" />
              Call (334) 692-5537
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Commercial;
